// src/store/slices/numberLinesSlice.ts
/**
 * Primary number line configuration with localStorage persistence
 * Implements Section 5.2 of the specification
 * Phase 3: Enhanced with NumberFormatConfig
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumberLineState, Fraction, NumberFormatConfig, defaultNumberFormatConfig } from '../../types/redux';

const NUMBER_LINE_STORAGE_KEY = 'nl:numberLine';

// Default number line state with Phase 3 enhancements
const defaultNumberLineState: NumberLineState = {
  id: 'primary',
  unitInterval: { n: 1, d: 1 }, // integers by default
  tickMode: 'labeled',
  labelPosition: 'below',
  numberType: 'integer',
  numberFormat: { ...defaultNumberFormatConfig, type: 'integer' },
  fractionDisplay: {
    format: 'mixed',
    simplify: true,
  },
  subTickDivisions: null,
};

/**
 * Migrate legacy state to Phase 3 format
 */
function migrateLegacyState(stored: any): NumberLineState {
  const state = { ...defaultNumberLineState, ...stored };
  
  // Migrate old numberType to new numberFormat
  if (stored.numberType && !stored.numberFormat) {
    const typeMap: Record<string, NumberFormatConfig['type']> = {
      'integer': 'integer',
      'decimal': 'decimal',
      'fraction': 'fraction',
      'percent': 'percentage',
    };
    
    state.numberFormat = {
      ...defaultNumberFormatConfig,
      type: typeMap[stored.numberType] || 'integer',
      decimalPlaces: 2,
      fractionFormat: stored.fractionDisplay?.format || 'mixed',
      fractionSimplify: stored.fractionDisplay?.simplify !== false,
    };
  } else if (!stored.numberFormat) {
    // Ensure numberFormat exists even if no migration needed
    state.numberFormat = { ...defaultNumberFormatConfig };
  }
  
  return state;
}

/**
 * Load number line state from localStorage with migration
 */
function loadNumberLineFromStorage(): NumberLineState {
  try {
    const stored = localStorage.getItem(NUMBER_LINE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const migrated = migrateLegacyState(parsed);
      
      // Check if migration happened
      if (parsed.numberType && !parsed.numberFormat) {
        saveNumberLineToStorage(migrated);
      }
      
      return migrated;
    }
  } catch (error) {
    console.warn('Failed to load number line from storage:', error);
  }
  return defaultNumberLineState;
}

/**
 * Save number line state to localStorage
 */
function saveNumberLineToStorage(numberLine: NumberLineState): void {
  try {
    localStorage.setItem(NUMBER_LINE_STORAGE_KEY, JSON.stringify(numberLine));
  } catch (error) {
    console.warn('Failed to save number line to storage:', error);
  }
}

const initialState: NumberLineState = loadNumberLineFromStorage();

export const numberLinesSlice = createSlice({
  name: 'numberLines',
  initialState,
  reducers: {
    /**
     * Set the unit interval (spacing between major ticks)
     */
    setUnitInterval: (state, action: PayloadAction<Fraction>) => {
      state.unitInterval = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set tick display mode
     */
    setTickMode: (state, action: PayloadAction<'none' | 'ticks' | 'labeled'>) => {
      state.tickMode = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set label position relative to the line
     */
    setLabelPosition: (state, action: PayloadAction<'below' | 'above'>) => {
      state.labelPosition = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set number type display (DEPRECATED - use setFormatType)
     */
    setNumberType: (state, action: PayloadAction<'integer' | 'decimal' | 'fraction' | 'percent'>) => {
      // Migrate to new format
      const typeMap: Record<string, NumberFormatConfig['type']> = {
        'integer': 'integer',
        'decimal': 'decimal',
        'fraction': 'fraction',
        'percent': 'percentage',
      };
      if (state.numberFormat) {
        state.numberFormat.type = typeMap[action.payload] || 'integer';
      }
      state.numberType = action.payload; // Keep for backward compatibility
      saveNumberLineToStorage(state);
    },

    /**
     * Toggle fraction format between mixed and improper (DEPRECATED - use updateNumberFormatProperty)
     */
    toggleFractionFormat: (state) => {
      if (state.fractionDisplay) {
        state.fractionDisplay.format = 
          state.fractionDisplay.format === 'mixed' ? 'improper' : 'mixed';
      }
      if (state.numberFormat) {
        state.numberFormat.fractionFormat = 
          (state.numberFormat.fractionFormat === 'mixed' ? 'improper' : 'mixed');
      }
      saveNumberLineToStorage(state);
    },

    /**
     * Toggle fraction simplification (DEPRECATED - use updateNumberFormatProperty)
     */
    toggleFractionSimplify: (state) => {
      if (state.fractionDisplay) {
        state.fractionDisplay.simplify = !state.fractionDisplay.simplify;
      }
      if (state.numberFormat) {
        state.numberFormat.fractionSimplify = !state.numberFormat.fractionSimplify;
      }
      saveNumberLineToStorage(state);
    },

    /**
     * Set complete number format configuration
     */
    setNumberFormat: (state, action: PayloadAction<NumberFormatConfig>) => {
      state.numberFormat = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Update specific number format property
     */
    updateNumberFormatProperty: <K extends keyof NumberFormatConfig>(
      state: NumberLineState,
      action: PayloadAction<{ key: K; value: NumberFormatConfig[K] }>
    ) => {
      const { key, value } = action.payload;
      if (state.numberFormat) {
        state.numberFormat[key] = value as any;
        saveNumberLineToStorage(state);
      }
    },

    /**
     * Set format type (convenience action)
     */
    setFormatType: (state, action: PayloadAction<NumberFormatConfig['type']>) => {
      if (state.numberFormat) {
        state.numberFormat.type = action.payload;
        saveNumberLineToStorage(state);
      }
    },

    /**
     * Set decimal places (0-3 or 'auto')
     */
    setDecimalPlaces: (state, action: PayloadAction<0 | 1 | 2 | 3 | 'auto'>) => {
      if (state.numberFormat) {
        state.numberFormat.decimalPlaces = action.payload;
        saveNumberLineToStorage(state);
      }
    },

    /**
     * Set currency symbol
     */
    setCurrencySymbol: (state, action: PayloadAction<string>) => {
      if (state.numberFormat) {
        state.numberFormat.currencySymbol = action.payload;
        saveNumberLineToStorage(state);
      }
    },

    /**
     * Set custom unit
     */
    setCustomUnit: (state, action: PayloadAction<string>) => {
      if (state.numberFormat) {
        state.numberFormat.customUnit = action.payload;
        saveNumberLineToStorage(state);
      }
    },

    /**
     * Set subtick divisions (null for none)
     */
    setSubTickDivisions: (state, action: PayloadAction<number | null>) => {
      state.subTickDivisions = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Reset to default configuration
     */
    resetNumberLine: (state) => {
      Object.assign(state, defaultNumberLineState);
      saveNumberLineToStorage(state);
    },

    /**
     * Load number line state from shared scene
     */
    loadNumberLineState: (state, action: PayloadAction<NumberLineState>) => {
      Object.assign(state, action.payload);
      saveNumberLineToStorage(state);
    },
  },
});

export const {
  setUnitInterval,
  setTickMode,
  setLabelPosition,
  setNumberType,
  toggleFractionFormat,
  toggleFractionSimplify,
  setNumberFormat,
  updateNumberFormatProperty,
  setFormatType,
  setDecimalPlaces,
  setCurrencySymbol,
  setCustomUnit,
  setSubTickDivisions,
  resetNumberLine,
  loadNumberLineState,
} = numberLinesSlice.actions;

export default numberLinesSlice.reducer;